import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TransactionsPage from './pages/TransactionsPage';
import CategoriesPage from './pages/CategoriesPage';
import BankStatementsPage from './pages/BankStatementsPage';
import BanksPage from './pages/BanksPage';
import UnknownTransactionsPage from './pages/UnknownTransactionsPage';
import TransactionApprovalsPage from './pages/TransactionApprovalsPage';
import PlannedExpensesPage from './pages/PlannedExpensesPage';
import SettingsPage from './pages/SettingsPage';
import DebugPage from './pages/DebugPage';
import CollectionsPage from './pages/CollectionsPage';
import BillsPage from './pages/BillsPage';
import FoldersPage from './pages/FoldersPage';
import Layout from './components/Layout';
import notificationService from './services/notifications';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4338ca',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f43f5e',
      light: '#fb7185',
      dark: '#be123c',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#f1f5f9',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.5px' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, textTransform: 'none' as const },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 20px',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(99,102,241,0.25)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
          border: '1px solid rgba(148,163,184,0.12)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f8fafc',
            fontWeight: 600,
            color: '#475569',
            fontSize: '0.75rem',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.72rem' },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' as const },
    },
    MuiSelect: {
      defaultProps: { size: 'small' as const },
    },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('PrivateRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);
  
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <div>Loading...</div>
      </Box>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

function App() {
  // Initialize PWA and notifications
  useEffect(() => {
    // Initialize service worker and request notification permission
    if (notificationService.isSupported()) {
      notificationService.initialize().then((initialized) => {
        if (initialized) {
          console.log('PWA initialized successfully');
        }
      });
    }
  }, []);

  // Debug: Log localStorage on app start
  React.useEffect(() => {
    console.log('App mounted - localStorage check:');
    console.log('Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
    console.log('User:', localStorage.getItem('user') ? 'Present' : 'Missing');
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/debug" element={<DebugPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="unknown-transactions" element={<UnknownTransactionsPage />} />
              <Route path="transaction-approvals" element={<TransactionApprovalsPage />} />
              <Route path="planned-expenses" element={<PlannedExpensesPage />} />
              <Route path="collections" element={<CollectionsPage />} />
              <Route path="bills" element={<BillsPage />} />
              <Route path="folders" element={<FoldersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="bank-statements" element={<BankStatementsPage />} />
              <Route path="banks" element={<BanksPage />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
